/**
 * Speedies (F-129): the member-side data layer. Types mirror the hermes-alfie functions exactly
 * (migrations 696, 698, 699; edge functions media-upload, speedy-search). Checked against the
 * built code in `.scratch/2026-10-16-speedies/sim/A3-ALIGN.md` §1.
 *
 * Every read goes through the viewer's own access: a free-plan member only ever gets free
 * Speedies, filtered by the server, never here. Nothing in this file awards points.
 */
import { supabase } from '@/lib/supabase';
import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';

// ── Types ──────────────────────────────────────────────────────────────────────────────

export type SpeedyStatus =
  | 'uploading'
  | 'processing'
  | 'reading'
  | 'checking'
  | 'published'
  | 'held'
  | 'rejected'
  | 'unpublished'
  | 'upload_expired'
  | 'failed'
  | 'deleted';

/** Statuses that will still change on their own (the progress screens keep listening). */
export const SPEEDY_IN_FLIGHT: readonly SpeedyStatus[] = ['uploading', 'processing', 'reading', 'checking'];

/** What a member sees for each status (A plan §4.6, "My Speedies"). */
export const SPEEDY_STATUS_LABEL: Record<SpeedyStatus, string> = {
  uploading: 'Uploading',
  processing: 'Processing video',
  reading: 'Reading your Speedy',
  checking: 'Checking',
  published: 'Live',
  held: 'With a reviewer',
  rejected: 'Not approved',
  unpublished: 'Taken down',
  upload_expired: "Upload didn't finish",
  failed: "Couldn't process this video",
  deleted: 'Deleted',
};

/** A row of the `speedy_cards` view. */
export interface SpeedyCard {
  id: string;
  status: SpeedyStatus;
  title: string | null;
  caption: string | null;
  author: { id: string; name: string | null; avatar_url: string | null };
  group_key: string | null;
  group_label: string | null;
  job_key: string | null;
  job_label: string | null;
  second_topic_key: string | null;
  media_id: string | null;
  duration_s: number | null;
  width: number | null;
  height: number | null;
  /** NULL unless published. */
  poster_url: string | null;
  small_poster_url: string | null;
  preview_clip_url: string | null;
  blurhash: string | null;
  /** The Stream copy is verified; no pass is asked for before this. */
  playable: boolean;
  view_count: number;
  has_safety_risk: boolean;
  /** At most 5, in position order. */
  shown_tags: { key: string; label: string }[];
  series_id: string | null;
  series_order: number | null;
  published_at: string | null;
  created_at: string;
  is_mine: boolean;
  /** Super admins only; NULL for everyone else. */
  is_free: boolean | null;
  /** App only, never from the server: the member's own clip while their upload is on its way. */
  local_clip_url?: string;
}

export interface SpeedyFeedCard extends SpeedyCard {
  finished_by_me: boolean;
  saved_by_me: boolean;
}

export interface SpeedyDetails {
  card: SpeedyCard;
  problem_solved: string | null;
  job_label: string | null;
  second_topic_label: string | null;
  processes: string[];
  materials: string[];
  tools: string[];
  building_system: string | null;
  safety: { label: string; source: 'said' | 'seen'; high_risk: boolean }[];
  regulations: { label: string; source_url: string | null; states: string[] | null }[];
  states: string[] | null;
  chapters: { start_s: number; title: string }[];
  transcript: { start_s: number; end_s: number; text: string }[];
  /** Course and lesson titles are NULL here: the app reads them under the course rules. */
  links: { type: 'course' | 'lesson' | 'speedy'; id: string; title: string | null }[];
  series: { id: string; title: string; position: number | null; count: number } | null;
}

export type SpeedyLength = 'lt1' | '1to3' | '3to10';
export type SpeedySort = 'relevance' | 'newest' | 'most_viewed' | 'shortest';

export interface SpeedyFilters {
  group_keys?: string[];
  tag_keys?: string[];
  lengths?: SpeedyLength[];
}

export interface SpeedySearchRequest {
  q?: string;
  filters?: SpeedyFilters;
  sort?: SpeedySort;
  suggestion_picked?: string;
  limit?: number;
  offset?: number;
}

export interface SpeedySearchResponse {
  /** NULL if the search log couldn't be written; then no click is logged. */
  log_id: string | null;
  mode: 'semantic' | 'tag_only' | 'filters_only';
  total: number;
  results: SpeedyFeedCard[];
}

export interface SpeedyFacets {
  groups: { key: string; label: string | null; n: number }[];
  tags: { key: string; label: string; n: number }[];
  lengths: Record<SpeedyLength, number>;
}

export interface SpeedyTagSuggestion {
  tag_key: string;
  label: string;
  speedy_count: number;
}

export interface SpeedySeriesTile {
  id: string;
  title: string;
  description: string | null;
  color: 'teal' | 'amber' | 'rose' | 'violet' | 'sky' | 'lime';
  pattern: 'dots' | 'lines' | 'grid' | 'waves';
  item_count: number;
  finished_count: number;
  next_speedy_id: string | null;
  poster_url: string | null;
}

/** The member's own row. Only these columns are granted to members. */
export interface MySpeedy {
  id: string;
  title: string | null;
  status: SpeedyStatus;
  created_at: string;
  published_at: string | null;
  rejection_final: boolean;
}
const MY_SPEEDY_COLUMNS = 'id, title, status, created_at, published_at, rejection_final';

export interface MySpeedyRejection {
  reason_code: string;
  member_message: string;
  decided_at: string | null;
  final: boolean;
  /** Not final: days left to reply or re-record. Final: days until the video is removed. */
  days_left: number | null;
  updates: { body: string; created_at: string }[];
}

export interface SpeedyLiveStatus {
  speedy_id: string;
  created_by: string;
  status: SpeedyStatus;
  stage: string | null;
  updated_at: string;
}

export interface SpeedyCompletion {
  completed: boolean;
  points: number;
  reason?: 'not_published' | 'no_view' | 'too_fast';
}

// ── Errors ─────────────────────────────────────────────────────────────────────────────

/** A refusal from the server, with its code (an edge `error` or the database message). */
export class SpeedyApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number | null = null,
  ) {
    super(message);
    this.name = 'SpeedyApiError';
  }
}

/** The database raises plain codes (`update_open`, `stale_read`, …); keep the code and message. */
function fromPostgrest(error: { message: string; code?: string }): SpeedyApiError {
  const code = /^[a-z_]+/.exec(error.message)?.[0] ?? error.code ?? 'error';
  return new SpeedyApiError(code, error.message);
}

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated types lag these RPCs
  const { data, error } = await (supabase.rpc as any)(fn, args);
  if (error) throw fromPostgrest(error);
  return data as T;
}

/** POST to a Speedy edge function as the signed-in member; `{error, message}` becomes an error. */
export async function callSpeedyEdge<T>(
  fn: 'media-upload' | 'speedy-search' | 'speedy-admin' | 'speedy-list-draft',
  body: Record<string, unknown>,
  action?: string,
  signal?: AbortSignal,
): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new SpeedyApiError('unauthorized', 'Sign in again.', 401);
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${fn}${action ? `?action=${action}` : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...EDGE_FUNCTION_HEADERS, 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal,
  });
  const json = await res.json().catch(() => null) as (T & { error?: string; message?: string }) | null;
  if (!res.ok) {
    throw new SpeedyApiError(
      json?.error ?? `http_${res.status}`,
      json?.message ?? "Something went wrong. Try again.",
      res.status,
    );
  }
  return json as T;
}

// ── Reads ──────────────────────────────────────────────────────────────────────────────

/**
 * A page of the feed: the newest published Speedies before `before`, unfinished first. The next
 * page's `before` is the OLDEST `published_at` on this page (`oldestPublishedAt`), never the last
 * card's: the last card is the oldest finished one.
 */
export function fetchSpeedyFeed(opts: { limit?: number; before?: string | null; excludeFinished?: boolean }) {
  return rpc<SpeedyFeedCard[]>('speedy_feed_for_me', {
    p_limit: opts.limit ?? 20,
    p_before: opts.before ?? null,
    p_exclude_finished: opts.excludeFinished ?? false,
  }).then((rows) => rows ?? []);
}

export function oldestPublishedAt(page: { published_at: string | null }[]): string | null {
  let oldest: string | null = null;
  for (const c of page) if (c.published_at && (!oldest || c.published_at < oldest)) oldest = c.published_at;
  return oldest;
}

export function searchSpeedies(req: SpeedySearchRequest, signal?: AbortSignal) {
  return callSpeedyEdge<SpeedySearchResponse>('speedy-search', { ...req }, undefined, signal);
}

export function fetchSpeedyFacets(filters: SpeedyFilters) {
  return rpc<SpeedyFacets>('speedy_facets', { p_filters: filters });
}

export function fetchSpeedyTagSuggestions(prefix: string, limit = 5) {
  return rpc<SpeedyTagSuggestion[]>('speedy_tag_list_for_me', { p_prefix: prefix, p_limit: limit })
    .then((rows) => rows ?? []);
}

export function fetchSpeedySeries() {
  return rpc<SpeedySeriesTile[]>('speedy_series_for_me').then((rows) => rows ?? []);
}

export function fetchSpeedyDetails(speedyId: string, organizationId: string | null) {
  return rpc<SpeedyDetails | null>('get_speedy_details', {
    p_speedy_id: speedyId,
    p_organization_id: organizationId,
  });
}

/** Cards by id, as the viewer may see them (hidden ones simply don't come back). */
export async function fetchSpeedyCards(ids: string[]): Promise<SpeedyCard[]> {
  if (ids.length === 0) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the view postdates the generated types
  const { data, error } = await (supabase as any).from('speedy_cards').select('*').in('id', ids);
  if (error) throw fromPostgrest(error);
  return (data ?? []) as SpeedyCard[];
}

/**
 * Adds the viewer's own `finished_by_me` and `saved_by_me` to cards from `speedy_cards`, read the
 * same way `speedy-search` reads them (own `speedy_views` with `completed_at`, own `speedy_saves`).
 */
export async function withMyFlags(userId: string, cards: SpeedyCard[]): Promise<SpeedyFeedCard[]> {
  if (cards.length === 0) return [];
  const ids = cards.map((c) => c.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;
  const [views, saves] = await Promise.all([
    db.from('speedy_views').select('speedy_id').eq('user_id', userId).in('speedy_id', ids).not('completed_at', 'is', null),
    db.from('speedy_saves').select('speedy_id').eq('user_id', userId).in('speedy_id', ids),
  ]);
  if (views.error) throw fromPostgrest(views.error);
  if (saves.error) throw fromPostgrest(saves.error);
  const finished = new Set(((views.data ?? []) as { speedy_id: string }[]).map((r) => r.speedy_id));
  const saved = new Set(((saves.data ?? []) as { speedy_id: string }[]).map((r) => r.speedy_id));
  return cards.map((c) => ({ ...c, finished_by_me: finished.has(c.id), saved_by_me: saved.has(c.id) }));
}

/** A series' published Speedies in series order, with the viewer's flags. */
export async function fetchSeriesCards(userId: string, seriesId: string): Promise<SpeedyFeedCard[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).from('speedy_cards').select('*')
    .eq('series_id', seriesId).eq('status', 'published').order('series_order', { ascending: true }).limit(200);
  if (error) throw fromPostgrest(error);
  return withMyFlags(userId, (data ?? []) as SpeedyCard[]);
}

/** The `/speedy` picker: live Speedies whose title has the text, newest first (an empty text: newest). */
export async function fetchSpeedyPicker(q: string, limit = 8): Promise<SpeedyCard[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query = (supabase as any).from('speedy_cards').select('*').eq('status', 'published')
    .order('published_at', { ascending: false }).limit(limit);
  const t = q.trim();
  if (t) query = query.ilike('title', `%${t.replace(/[%_\\]/g, (m: string) => `\\${m}`)}%`);
  const { data, error } = await query;
  if (error) throw fromPostgrest(error);
  return (data ?? []) as SpeedyCard[];
}

/** The member's saved Speedies, newest saved first. */
export async function fetchSavedSpeedies(userId: string): Promise<SpeedyFeedCard[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).from('speedy_saves').select('speedy_id, created_at')
    .eq('user_id', userId).order('created_at', { ascending: false }).limit(200);
  if (error) throw fromPostgrest(error);
  const ids = ((data ?? []) as { speedy_id: string }[]).map((r) => r.speedy_id);
  const cards = await fetchSpeedyCards(ids);
  const byId = new Map(cards.map((c) => [c.id, c]));
  const kept = ids.map((id) => byId.get(id)).filter((c): c is SpeedyCard => !!c && c.status === 'published');
  return withMyFlags(userId, kept);
}

export async function fetchMySpeedies(userId: string): Promise<MySpeedy[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).from('speedies').select(MY_SPEEDY_COLUMNS)
    .eq('created_by', userId).neq('status', 'deleted').order('created_at', { ascending: false }).limit(100);
  if (error) throw fromPostgrest(error);
  return (data ?? []) as MySpeedy[];
}

/** One row per Speedy of the caller's (migration 709): views, saves, people in the last 24 hours. */
export interface MySpeedyCardStats {
  speedy_id: string;
  views: number;
  saves: number;
  people_24h: number;
}

export function fetchMySpeedyCardStats() {
  return rpc<MySpeedyCardStats[]>('my_speedy_card_stats');
}

/** The insights panel for one of the caller's Speedies (709). The author's own watching is left out. */
export interface MySpeedyInsights {
  views: number;
  saves: number;
  /** People who watched (each once, however often). */
  people: number;
  people_24h: number;
  replays: number;
  /** NULL while nobody has watched. */
  avg_watched_pct: number | null;
  /** Percent of the people who got a quarter in, 60% (finished) and to the end; NULL while nobody has. */
  reached: { quarter: number; most: number; end: number } | null;
  /** New viewers on each of the last 7 days (Sydney days), oldest first. */
  by_day: { day: string; people: number }[];
}

export function fetchMySpeedyInsights(speedyId: string) {
  return rpc<MySpeedyInsights>('my_speedy_insights', { p_speedy_id: speedyId });
}

export function fetchMyRejection(speedyId: string) {
  return rpc<MySpeedyRejection | null>('get_my_speedy_rejection', { p_speedy_id: speedyId });
}

// ── Writes ─────────────────────────────────────────────────────────────────────────────

export function recordSpeedyView(speedyId: string, checkpoint: 3 | 25 | 100) {
  return rpc<void>('record_speedy_view', { p_speedy_id: speedyId, p_checkpoint: checkpoint });
}

export function recordSpeedyCompletion(speedyId: string) {
  return rpc<SpeedyCompletion>('record_speedy_completion', { p_speedy_id: speedyId });
}

export async function setSpeedySaved(userId: string, speedyId: string, saved: boolean): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const table = (supabase as any).from('speedy_saves');
  const { error } = saved
    ? await table.upsert({ user_id: userId, speedy_id: speedyId }, { onConflict: 'user_id,speedy_id', ignoreDuplicates: true })
    : await table.delete().eq('user_id', userId).eq('speedy_id', speedyId);
  if (error) throw fromPostgrest(error);
}

export function logSpeedySearchClick(logId: string, speedyId: string, rank: number) {
  return rpc<void>('log_speedy_search_click', { p_log_id: logId, p_speedy_id: speedyId, p_rank: rank });
}

/** Title and up to 5 tags, while the Speedy is uploading or processing. A blank title never writes. */
export function updateMySpeedyDetails(speedyId: string, title: string | null, tags: string[]) {
  return rpc<void>('update_my_speedy_details', {
    p_speedy_id: speedyId,
    p_title: title && title.trim() ? title.trim() : null,
    p_tags: tags.slice(0, 5),
  });
}

export function deleteSpeedy(speedyId: string) {
  return rpc<void>('delete_speedy', { p_speedy_id: speedyId });
}

/** The words a member sees for a refusal from `delete_speedy`. */
export function deleteSpeedyErrorMessage(err: unknown): string {
  const code = err instanceof SpeedyApiError ? err.code : '';
  if (code === 'not_found') return 'This Speedy is already gone.';
  if (code === 'use_abort') return 'This Speedy is still uploading. Cancel it from the upload screen.';
  return "Couldn't delete. Try again.";
}

export function submitSpeedyUpdate(speedyId: string, text: string) {
  return rpc<{ status: SpeedyStatus }>('submit_speedy_update', { p_speedy_id: speedyId, p_text: text });
}

/** The words a member sees for a refusal from `submit_speedy_update`. */
export function speedyUpdateErrorMessage(err: unknown): string {
  const code = err instanceof SpeedyApiError ? err.code : '';
  switch (code) {
    case 'update_open':
      return 'Your last update is still with a reviewer.';
    case 'not_open':
      return "This Speedy can't take an update any more. You can record it again.";
    case 'not_found':
      return "This Speedy isn't available.";
    default:
      return err instanceof SpeedyApiError && /1–1000/.test(err.message)
        ? 'An update is 1 to 1,000 characters.'
        : "Couldn't send. Try again.";
  }
}

// ── Upload (`media-upload`, mode `r2-multipart`; built in media-upload/speedy.ts) ──────────

export type SpeedyUploadMime = 'video/mp4' | 'video/quicktime' | 'video/webm';

export interface SpeedyUploadLimits {
  can_create: boolean;
  unfinished: number;
  unfinished_max: number;
  today: number;
  today_max: number;
}

export interface SpeedyUploadInit {
  speedy_id: string;
  media_id: string;
  part_size_bytes: number;
  parts: { part_number: number; url: string }[];
  parts_expire_at: string;
  expires_at: string;
  limits: SpeedyUploadLimits;
}

const UPLOAD = { mode: 'r2-multipart', purpose: 'speedy_video' } as const;
/** An upload call that hasn't answered in this time is treated as failed (and can be retried). */
const UPLOAD_CALL_TIMEOUT_MS = 60_000;
const uploadTimeout = () => (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(UPLOAD_CALL_TIMEOUT_MS) : undefined);

export function speedyUploadLimits() {
  return callSpeedyEdge<SpeedyUploadLimits>('media-upload', { ...UPLOAD }, 'limits', uploadTimeout());
}

export function speedyUploadInit(fields: {
  size_bytes: number;
  duration_s: number;
  width: number;
  height: number;
  mime_type: SpeedyUploadMime;
  replaces_speedy_id?: string;
  /** One per "Post Speedy", sent again on a retry: the server returns the same Speedy (708). */
  init_key: string;
}) {
  return callSpeedyEdge<SpeedyUploadInit>('media-upload', { ...UPLOAD, ...fields, licence_ack: true }, 'init', uploadTimeout());
}

/** Fresh part links, or with `[]` the parts R2 already has. */
export function speedyUploadParts(speedyId: string, partNumbers: number[]) {
  return callSpeedyEdge<{
    parts: { part_number: number; url: string }[];
    parts_expire_at: string;
    uploaded?: { part_number: number; etag: string; size: number }[];
  }>('media-upload', { ...UPLOAD, speedy_id: speedyId, part_numbers: partNumbers }, 'parts', uploadTimeout());
}

export function speedyUploadComplete(speedyId: string) {
  return callSpeedyEdge<{ speedy_id: string; status: SpeedyStatus }>('media-upload', { ...UPLOAD, speedy_id: speedyId }, 'complete', uploadTimeout());
}

/** Cancels an upload. `rejected` means a re-record was cancelled and the old Speedy is back. */
export function speedyUploadAbort(speedyId: string) {
  return callSpeedyEdge<{ speedy_id: string; status: 'deleted' | 'rejected' }>('media-upload', { ...UPLOAD, speedy_id: speedyId }, 'abort', uploadTimeout());
}

/**
 * The browser's type for the server's three, or null (the server takes no other). Codec details
 * are dropped (`video/webm;codecs=vp9,opus`). A file with no type (some phone cameras) goes by
 * its name.
 */
export function speedyUploadMime(type: string, name?: string): SpeedyUploadMime | null {
  const base = type.split(';')[0].trim().toLowerCase();
  if (base === 'video/mp4' || base === 'video/x-m4v') return 'video/mp4';
  if (base === 'video/quicktime') return 'video/quicktime';
  if (base === 'video/webm') return 'video/webm';
  if (base) return null;
  const ext = /\.([a-z0-9]+)$/i.exec(name ?? '')?.[1]?.toLowerCase();
  if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'webm') return 'video/webm';
  return null;
}

/** What a member sees for an upload refusal: the server's own sentence, except these. */
export function speedyUploadErrorMessage(err: unknown): string {
  const e = err instanceof SpeedyApiError ? err : null;
  switch (e?.code) {
    case 'too_many_open':
      return 'You have 3 Speedies on their way. Wait for one to finish.';
    case 'daily_limit':
      return "That's 20 today. Try again tomorrow.";
    case 'expired':
      return "Upload didn't finish. Try again.";
    default:
      return e?.message || "Couldn't upload. Try again.";
  }
}

// ── Query keys ─────────────────────────────────────────────────────────────────────────

export const speedyKeys = {
  all: ['speedy'] as const,
  feedTiles: () => ['speedy', 'feed-tiles'] as const,
  feed: () => ['speedy', 'feed'] as const,
  search: (req: SpeedySearchRequest) => ['speedy', 'search', req] as const,
  facets: (filters: SpeedyFilters) => ['speedy', 'facets', filters] as const,
  tags: (prefix: string) => ['speedy', 'tags', prefix] as const,
  series: () => ['speedy', 'series'] as const,
  details: (id: string, orgId: string | null) => ['speedy', 'details', id, orgId] as const,
  cards: (ids: string[]) => ['speedy', 'cards-by-id', [...ids].sort()] as const,
  saved: (userId: string) => ['speedy', 'saved', userId] as const,
  seriesCards: (seriesId: string) => ['speedy', 'series-cards', seriesId] as const,
  mine: (userId: string) => ['speedy', 'mine', userId] as const,
  mineStats: (userId: string) => ['speedy', 'mine-stats', userId] as const,
  insights: (speedyId: string) => ['speedy', 'insights', speedyId] as const,
  rejection: (id: string) => ['speedy', 'rejection', id] as const,
};
